import { type ReactNode, useEffect, useState, createContext, useContext } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import {
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { isAuthEnabled } from '@/config/env';
import { msalConfig, loginRequest } from '@/config/auth';
import { setMsalInstance } from '@/api/msalInstance';

// ─── Auth Context ────────────────────────────────────────────────────────────

interface AuthContextValue {
  userEmail: string | null;
  userName: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  userEmail: null,
  userName: null,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

// ─── MSAL Instance ───────────────────────────────────────────────────────────

const msalInstance = isAuthEnabled ? new PublicClientApplication(msalConfig) : null;

if (msalInstance) {
  setMsalInstance(msalInstance);
}

// ─── Loading Screen ──────────────────────────────────────────────────────────

const useStyles = makeStyles({
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: tokens.spacingVerticalM,
  },
});

function LoadingScreen() {
  const styles = useStyles();
  return (
    <div className={styles.loading}>
      <Spinner size="medium" />
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        Redirecting to sign in...
      </Text>
    </div>
  );
}

// ─── Login Redirect ──────────────────────────────────────────────────────────

function LoginRedirect() {
  const { inProgress, instance } = useMsal();

  if (inProgress === InteractionStatus.None) {
    void instance.loginRedirect(loginRequest);
  }

  return <LoadingScreen />;
}

// ─── Auth Gate (provides user info from MSAL account) ────────────────────────

function AuthenticatedApp({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const account = accounts[0] ?? null;

  return (
    <AuthContext.Provider
      value={{
        userEmail: account?.username ?? null,
        userName: account?.name ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function AuthGate({ children }: { children: ReactNode }) {
  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedApp>{children}</AuthenticatedApp>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginRedirect />
      </UnauthenticatedTemplate>
    </>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────

// Dev user for local development
const DEV_AUTH: AuthContextValue = {
  userEmail: 'bgay@ata.net',
  userName: 'Barrett Gay',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (msalInstance) {
      void msalInstance.initialize().then(() => setIsInitialized(true));
    }
  }, []);

  // Dev mode — bypass auth
  if (!isAuthEnabled || !msalInstance) {
    return (
      <AuthContext.Provider value={DEV_AUTH}>
        {children}
      </AuthContext.Provider>
    );
  }

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
}
